import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import {
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQuery,
} from "../../../hooks";
import {
  IBusinessType,
  IOnboardingStatus,
  IOnboardingStep1Request,
  IOnboardingStepResponse,
  IRevenueBracket,
} from "../../../interfaces-and-types";
import { globalHandlerUtils } from "../../../utils";
import { IOnboardingStepProps, IOnboardingStepViewProps } from "../Onboarding";

import { View } from "./View";

export interface IOnboardingStep1ViewProps
  extends IOnboardingStepViewProps<IOnboardingStep1Request> {
  registered_countryFormValue: string;
  isLoadingBusinessTypes: boolean;
  businessTypes: IBusinessType[];
}

export const OnboardingStep1 = (props: IOnboardingStepProps) => {
  const {
    isLoading: isLoadingOnboardingStatus,
    isSuccess: isSuccessOnboardingStatus,
    data: onboardingStatus,
  } = useMerchantApiQuery<IOnboardingStatus>("getOnboardingStatus");

  const {
    isLoading: isLoadingStep1,
    isSuccess: isSuccessStep1,
    isError: isErrorStep1,
    data: apiResponse,
    mutate: saveOnboardingStep,
  } = useMerchantApiMutation<IOnboardingStep1Request, IOnboardingStepResponse>(
    "saveOnboardingStep1"
  ).usePostMutation;

  const {
    isLoading: isLoadingBusinessTypes,
    isSuccess: isSuccessBusinessTypes,
    data: businessTypes,
  } = useMerchantApiQuery<IBusinessType[]>("getBusinessTypes");

  useEffect(() => {
    if (isSuccessStep1) {
      // display success message and request email confirmation
      props.handleNext();
    }
  }, [isSuccessStep1]);

  const initialFormValues: IOnboardingStep1Request = {
    registered_country: "",
    registered_business_type_id: "",
    registration_number: "",
  };

  const onboardingStep1Schema: yup.SchemaOf<IOnboardingStep1Request> = yup
    .object({
      registered_country: yup
        .string()
        .required("Country is required")
        .nullable(),
      registered_business_type_id: yup
        .string()
        .required("Business Type is required"),
      registration_number: yup
        .string()
        .nullable()
        .optional()
        .when("registered_country", {
          is: (registered_country: string) => registered_country === "NG",
          then: yup.string().required("Registration Number is required"),
        }),
    })
    .required();

  const handleApiSubmit = (data: IOnboardingStep1Request) => {
    saveOnboardingStep(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, reset, watch } = useHookForm({
    initialFormValues,
    validationSchema: onboardingStep1Schema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  const registered_countryFormValue = watch("registered_country");

  useEffect(() => {
    if (isSuccessOnboardingStatus) {
      reset(onboardingStatus); // set retrieved values on the form
    }
  }, [isSuccessOnboardingStatus]);

  return (
    <View
      {...props}
      handleSubmit={onFormSubmit}
      registered_countryFormValue={registered_countryFormValue}
      register={register}
      errors={errors}
      isLoadingBusinessTypes={isLoadingBusinessTypes}
      businessTypes={businessTypes || []}
    />
  );
};
